import { colors as sampleColors, randomRegisteredColor } from './colors'
import { findColor, searchColors, type RegistryColor, type SearchParams, type SearchResult } from './registry'

/**
 * Registry data access. Talks to the Netlify Functions under /api (backed by
 * Netlify Database). If the API is unreachable, for example `vite` running
 * without the Netlify plugin, or the database not seeded yet, it falls back
 * to the bundled sample so the site always renders.
 */

const TIMEOUT_MS = 6000

async function get<T>(path: string, params: Record<string, string | number | undefined>): Promise<T> {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') qs.set(k, String(v))
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS)
  try {
    const res = await fetch(`/api/${path}?${qs}`, { signal: ctrl.signal, headers: { accept: 'application/json' } })
    if (!res.ok) throw new Error(`${path}: ${res.status}`)
    const ct = res.headers.get('content-type') ?? ''
    if (!ct.includes('application/json')) throw new Error(`${path}: not json`)
    return (await res.json()) as T
  } finally {
    clearTimeout(timer)
  }
}

/** A non-2xx answer from /api, with the parsed body when there was one. */
export class ApiError extends Error {
  status: number
  body: Record<string, unknown>
  constructor(path: string, status: number, body: Record<string, unknown>) {
    super(typeof body.message === 'string' ? body.message : `${path}: ${status}`)
    this.status = status
    this.body = body
  }
  /** Per-field messages the function returned, if any. */
  get errors(): Record<string, string> {
    const e = this.body.errors
    return e && typeof e === 'object' ? (e as Record<string, string>) : {}
  }
}

async function post<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS * 2)
  try {
    const res = await fetch(`/api/${path}`, {
      method: 'POST',
      signal: ctrl.signal,
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(body),
    })
    const ct = res.headers.get('content-type') ?? ''
    const data = ct.includes('application/json') ? ((await res.json()) as Record<string, unknown>) : {}
    if (!res.ok) throw new ApiError(path, res.status, data)
    return data as T
  } finally {
    clearTimeout(timer)
  }
}

let apiDown = false
function fallback<T>(fn: () => T): T {
  if (!apiDown && import.meta.env.DEV) console.info('[colormark] /api unavailable, using bundled sample data')
  apiDown = true
  return fn()
}

export async function search(params: SearchParams): Promise<SearchResult> {
  try {
    return await get<SearchResult>('search', {
      type: params.type,
      q: params.query,
      limit: params.limit,
      offset: params.offset,
      order: params.order,
    })
  } catch {
    return fallback(() => searchColors(sampleColors, params))
  }
}

export async function lookup(key: string): Promise<RegistryColor | null> {
  try {
    const r = await get<{ found: boolean; color?: RegistryColor }>('color', { key })
    return r.found && r.color ? r.color : null
  } catch {
    return fallback(() => findColor(sampleColors, key) ?? null)
  }
}

export async function random(n = 1): Promise<RegistryColor[]> {
  try {
    const r = await get<{ colors: RegistryColor[] }>('random', { n })
    if (r.colors.length) return r.colors
    throw new Error('empty')
  } catch {
    return fallback(() => Array.from({ length: n }, randomRegisteredColor))
  }
}

export interface Stats {
  total: number
  reserved: number
  registered: number
  premium: number
  available: number
}

export async function stats(): Promise<Stats | null> {
  try {
    return await get<Stats>('stats', {})
  } catch {
    return null
  }
}

/*
 * Registration and forms. These need the live API: there is no offline
 * fallback, so callers show an error when they throw.
 */

export interface CheckoutSession {
  id: string
  status: 'pending' | 'paid' | 'cancelled'
  provider: string
  hex: string
  name: string
  slug: string
  plan: { id: string; label: string; years: number; priceCents: number }
  amountCents: number
  currency: string
  firstName: string
  email: string
  color: RegistryColor | null
}

export interface CheckoutInput {
  hex: string
  name: string
  plan: string
  firstName: string
  lastName: string
  email: string
}

/** Starts a registration; returns the checkout page to send the customer to. */
export function createCheckout(input: CheckoutInput): Promise<{ id: string; url: string; provider: string }> {
  return post('checkout', { ...input })
}

export async function getCheckout(sessionId: string): Promise<CheckoutSession | null> {
  try {
    return await get<CheckoutSession>('checkout', { session_id: sessionId })
  } catch {
    return null
  }
}

/** Completes a mock checkout: no payment is taken. */
export async function fulfillCheckout(sessionId: string): Promise<CheckoutSession> {
  const r = await post<{ ok: boolean; session: CheckoutSession }>('fulfill', { session_id: sessionId })
  return r.session
}

export function sendContact(input: { firstName: string; lastName: string; email: string; subject: string; message: string; website?: string }): Promise<void> {
  return post<{ ok: boolean }>('contact', { ...input }).then(() => undefined)
}

export function subscribeNewsletter(email: string, source: string, website = ''): Promise<void> {
  return post<{ ok: boolean }>('newsletter', { email, source, website }).then(() => undefined)
}
